import React, { useState, useEffect, useRef } from 'react';
import { useStore } from './store';
import { NodeLibrary } from './NodeLibrary';
import { PropertyPanel } from './PropertyPanel';
import { Box, Settings, ChevronDown, ChevronRight } from 'lucide-react';

export const FlowSidebar = ({ isCollapsed, onToggle, onNodeSelect }) => {
  const [activeTab, setActiveTab] = useState('nodes');
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const setSelectedNodeId = useStore((s) => s.setSelectedNodeId);
  const prevSelectedNodeIdRef = useRef(null);

  useEffect(() => {
    if (selectedNodeId && selectedNodeId !== prevSelectedNodeIdRef.current) {
      setActiveTab('properties');
      if (onNodeSelect) onNodeSelect();
    }
    prevSelectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId, onNodeSelect]);

  const handlePropertiesClick = () => {
    if (activeTab === 'properties' && selectedNodeId) {
      setSelectedNodeId(null);
      setActiveTab('nodes');
    } else {
      setActiveTab('properties');
    }
  };

  return (
    <div
      className="relative bg-white border-l border-zinc-200 h-full flex flex-col shadow-xl z-50 shrink-0"
      style={{
        width: isCollapsed ? '0px' : '380px',
        minWidth: isCollapsed ? '0px' : '380px',
        overflow: 'visible',
        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), min-width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Toggle grip — lives inside the sidebar so it can never trigger ReactFlow events */}
      <style>{`
        .sidebar-grip-btn {
          position: absolute;
          left: -24px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 90px;
          background: white;
          border-left: 1px solid #e4e4e7;
          border-top: 1px solid #e4e4e7;
          border-bottom: 1px solid #e4e4e7;
          border-radius: 12px 0 0 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 60;
        }
        .sidebar-grip-btn::before {
          content: '';
          position: absolute;
          top: -12px;
          right: 0;
          width: 12px;
          height: 12px;
          background: transparent;
          border-bottom-right-radius: 12px;
          box-shadow: 6px 6px 0 6px white;
          pointer-events: none;
        }
        .sidebar-grip-btn::after {
          content: '';
          position: absolute;
          bottom: -12px;
          right: 0;
          width: 12px;
          height: 12px;
          background: transparent;
          border-top-right-radius: 12px;
          box-shadow: 6px -6px 0 6px white;
          pointer-events: none;
        }
        .sidebar-grip-top-border {
          position: absolute;
          top: -12px;
          right: 0;
          width: 12px;
          height: 12px;
          border-bottom-right-radius: 12px;
          border-bottom: 1px solid #e4e4e7;
          border-right: 1px solid #e4e4e7;
          pointer-events: none;
        }
        .sidebar-grip-bottom-border {
          position: absolute;
          bottom: -12px;
          right: 0;
          width: 12px;
          height: 12px;
          border-top-right-radius: 12px;
          border-top: 1px solid #e4e4e7;
          border-right: 1px solid #e4e4e7;
          pointer-events: none;
        }
      `}</style>
      <button
        className="sidebar-grip-btn"
        onClick={onToggle}
        title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
      >
        <div className="sidebar-grip-top-border" />
        <div className="sidebar-grip-bottom-border" />
        <ChevronRight
          size={20}
          strokeWidth={3}
          className="text-zinc-700"
          style={{
            transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            marginRight: '2px',
          }}
        />
      </button>

      {/* Inner content — fades out while collapsing to avoid clipping artifacts */}
      <div
        className="flex flex-col h-full"
        style={{
          opacity: isCollapsed ? 0 : 1,
          transition: 'opacity 180ms ease',
          minWidth: '380px',
          pointerEvents: isCollapsed ? 'none' : 'auto',
          overflow: 'hidden',
        }}
      >
        {/* Tabs Header */}
        <div className="flex p-2 bg-zinc-50/80 border-b border-zinc-200/80 gap-1.5">
          <button
            onClick={() => setActiveTab('nodes')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'nodes'
                ? 'bg-white text-zinc-900 shadow-2xs border border-zinc-200/90'
                : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100/70 border border-transparent'
            }`}
          >
            <Box size={14} className={activeTab === 'nodes' ? 'text-zinc-900' : 'text-zinc-400'} />
            Nodes
          </button>
          <button
            onClick={handlePropertiesClick}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === 'properties'
                ? 'bg-white text-zinc-900 shadow-2xs border border-zinc-200/90'
                : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100/70 border border-transparent'
            }`}
          >
            <Settings size={14} className={activeTab === 'properties' ? 'text-zinc-900' : 'text-zinc-400'} />
            Properties
            {activeTab === 'properties' && selectedNodeId && (
              <ChevronDown size={12} className="text-zinc-400 ml-0.5" />
            )}
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden relative">
          {activeTab === 'nodes' ? (
            <NodeLibrary />
          ) : (
            <PropertyPanel onClose={() => {
              if (!isCollapsed && onToggle) onToggle();
            }} />
          )}
        </div>
      </div>
    </div>
  );
};
